import { useEffect, useState } from 'react';

import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';
import { useShortLinkWidgetHiddenState } from '@hotjar/webapp/features/ShortLinkWidget';

/**
 * Wrapper to show the app interface on the components it wraps (the opposite to the NoAppInterfaceRoute wrapper).
 *
 * As we continue to migrate from Angular, we have some routes that used to show the interface based on angular
 * state changes. When we stop using those states, we may end up on a route without the main interface, so this
 * wrapper can be useful to show the interface in that situation.
 *
 * @param children
 * @param replaceInitial  Set this to false if the initial state should be set again when the user changes routes.
 */
export function WithAppInterfaceRoute({
  children,
  replaceInitial = true,
}: {
  children: React.ReactNode;
  replaceInitial?: boolean;
}): JSX.Element {
  const { setIsAppInterfaceVisible, isAppInterfaceVisible } = useUIState();
  const [initial] = useState(isAppInterfaceVisible);
  const { isHidden: isLinkWidgetHiddenInitial, setIsHidden: setIsShortLinkWidgetHidden } =
    useShortLinkWidgetHiddenState();

  useEffect(() => {
    setIsAppInterfaceVisible(true);

    // ensure the short link widget is visible
    setIsShortLinkWidgetHidden(false);
    return () => {
      if (!replaceInitial) {
        setIsAppInterfaceVisible(initial);
        setIsShortLinkWidgetHidden(isLinkWidgetHiddenInitial);
      }
    };
  }, [
    setIsAppInterfaceVisible,
    initial,
    setIsShortLinkWidgetHidden,
    replaceInitial,
    isLinkWidgetHiddenInitial,
  ]);

  return <>{children}</>;
}
